import { z } from 'zod';
import { countryCodeSchema, orgTypeSchema } from './orgs';

/*
 * Who can see and take a course, quiz or workshop ("Who can see this").
 * One rule for every kind of content, checked live on every request (see
 * the API's services/audience.ts):
 *
 * - public: anyone.
 * - org: members of the owner org and every org under it.
 * - org_only: members of the owner org itself.
 * - groups: members of the owner org in any of `scope.groupIds` (its groups).
 * - children: members of any org in `scope.orgIds` (orgs under the owner)
 *   or under them.
 * - targeted: members of any org matching `scope.countries` and
 *   `scope.orgTypes` (either may be empty); Nirvik's library only, for
 *   campaigns such as every university in India.
 */

export const AUDIENCES = ['public', 'org', 'org_only', 'groups', 'children', 'targeted'] as const;
export const audienceSchema = z.enum(AUDIENCES);
export type Audience = z.infer<typeof audienceSchema>;

export const audienceScopeSchema = z.object({
  groupIds: z.array(z.string().uuid()).max(50).optional(),
  orgIds: z.array(z.string().uuid()).max(50).optional(),
  countries: z.array(countryCodeSchema).max(60).optional(),
  orgTypes: z.array(orgTypeSchema).max(10).optional(),
});
export type AudienceScope = z.infer<typeof audienceScopeSchema>;

/** Which audiences an owner can choose: Nirvik's library has no members of its own. */
export const audiencesFor = (owner: 'library' | 'org'): Audience[] =>
  owner === 'library'
    ? ['public', 'targeted']
    : ['public', 'org', 'org_only', 'groups', 'children'];

export const AUDIENCE_LABELS: Record<Audience, string> = {
  public: 'Anyone with the link',
  org: 'Members, including child organizations',
  org_only: 'Members of this organization only',
  groups: 'Selected groups',
  children: 'Selected child organizations',
  targeted: 'Members of organizations by country or type',
};

/** Short form for list badges. */
export const AUDIENCE_BADGES: Record<Audience, string> = {
  public: 'Anyone with the link',
  org: 'Members only',
  org_only: 'This organization only',
  groups: 'Selected groups',
  children: 'Selected child organizations',
  targeted: 'Targeted organizations',
};

/** What's wrong with an audience for this owner, or null. Ids are checked by the API. */
export function audienceIssue(
  owner: 'library' | 'org',
  audience: Audience,
  scope: AudienceScope | null | undefined,
): string | null {
  if (!audiencesFor(owner).includes(audience))
    return owner === 'library'
      ? "Nirvik's library content is for everyone, or for organizations by country or type"
      : 'Pick who can see this';
  if (audience === 'groups' && !scope?.groupIds?.length) return 'Pick at least one group';
  if (audience === 'children' && !scope?.orgIds?.length)
    return 'Pick at least one child organization';
  if (audience === 'targeted' && !scope?.countries?.length && !scope?.orgTypes?.length)
    return 'Pick at least one country or organization type';
  return null;
}

/** Only the fields an audience uses (so switching audience never leaves stale ids behind). */
export function scopeFor(audience: Audience, scope: AudienceScope | null | undefined) {
  if (audience === 'groups') return { groupIds: [...new Set(scope?.groupIds ?? [])] };
  if (audience === 'children') return { orgIds: [...new Set(scope?.orgIds ?? [])] };
  if (audience === 'targeted')
    return {
      countries: [...new Set(scope?.countries ?? [])],
      orgTypes: [...new Set(scope?.orgTypes ?? [])],
    };
  return null;
}
